import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { sharpness, type Detection } from '../lib/receipt-detect';
import { analyzeFrame, prepareReceipt, release } from '../lib/receipt-photo';
import { haptic } from '../lib/telegram';
import { Icon } from './Icon';

/** How many steady detections in a row trigger the automatic capture (~1 s). */
const STEADY_FRAMES = 8;
const FRAME_MS = 120;
/** Longest side of the captured photo (bounded: iPhones cap canvas memory). */
const CAPTURE_MAX = 2400;

type Status = 'searching' | 'partial' | 'steady' | 'captured';

/** ImageCapture.takePhoto(): a full-resolution still (Chrome/Android); null elsewhere. */
async function takeStill(stream: MediaStream | null): Promise<ImageBitmap | null> {
  const track = stream?.getVideoTracks()[0];
  const Capture = (window as unknown as { ImageCapture?: new (track: MediaStreamTrack) => { takePhoto(): Promise<Blob> } })
    .ImageCapture;
  if (!track || !Capture) return null;
  const blob = await new Capture(track).takePhoto();
  return createImageBitmap(blob, { imageOrientation: 'from-image' });
}

export const cameraSupported = (): boolean =>
  typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);

/**
 * Full-screen receipt camera: finds the receipt in the live preview, waits
 * until it is steady and sharp, captures it automatically (or on the
 * shutter), then crops, straightens and cleans it up before `onCapture`.
 * Calls `onUnavailable` if the camera can't be opened, so the caller can fall
 * back to the photo picker.
 */
export function ReceiptCamera({
  onCapture,
  onClose,
  onPickPhoto,
  onUnavailable,
}: {
  onCapture: (photo: Blob) => void;
  onClose: () => void;
  onPickPhoto: () => void;
  onUnavailable: () => void;
}) {
  const { t } = useTranslation();
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const [detection, setDetection] = useState<Detection | null>(null);
  const [status, setStatus] = useState<Status>('searching');
  const [steady, setSteady] = useState(0);
  const [torch, setTorch] = useState<boolean | null>(null);
  const captured = useRef(false);
  const lastDetection = useRef<Detection | null>(null);

  // Open the back camera.
  useEffect(() => {
    let cancelled = false;
    navigator.mediaDevices
      // Ask for the sharpest the camera offers: small print needs the pixels.
      .getUserMedia({ audio: false, video: { facingMode: { ideal: 'environment' }, width: { ideal: 3840 }, height: { ideal: 2160 } } })
      .then(async (media) => {
        if (cancelled) return media.getTracks().forEach((track) => track.stop());
        stream.current = media;
        const [track] = media.getVideoTracks();
        const capabilities = (track?.getCapabilities?.() ?? {}) as MediaTrackCapabilities & { torch?: boolean };
        if (capabilities.torch) setTorch(false);
        if (video.current) {
          video.current.srcObject = media;
          await video.current.play().catch(() => undefined);
        }
      })
      .catch(() => {
        if (!cancelled) onUnavailable();
      });
    return () => {
      cancelled = true;
      stream.current?.getTracks().forEach((track) => track.stop());
    };
  }, [onUnavailable]);

  const capture = async (found: Detection | null) => {
    const el = video.current;
    if (!el || captured.current || !el.videoWidth) return;
    captured.current = true;
    setStatus('captured');
    haptic.success();
    let frame: HTMLCanvasElement | null = null;
    try {
      // A full-resolution still where the browser supports it (Android), else
      // the video frame, drawn at a bounded size (iPhone canvas memory is capped).
      const still = await takeStill(stream.current).catch(() => null);
      const source: CanvasImageSource = still ?? el;
      const sourceWidth = still?.width ?? el.videoWidth;
      const sourceHeight = still?.height ?? el.videoHeight;
      const scale = Math.min(1, CAPTURE_MAX / Math.max(sourceWidth, sourceHeight));
      frame = document.createElement('canvas');
      frame.width = Math.round(sourceWidth * scale);
      frame.height = Math.round(sourceHeight * scale);
      const context = frame.getContext('2d');
      if (!context) throw new Error('Canvas unavailable');
      context.drawImage(source, 0, 0, frame.width, frame.height);
      still?.close();
      stream.current?.getTracks().forEach((track) => track.stop());
      // Corners were found on the video frame; rescale them to the capture.
      const sx = frame.width / el.videoWidth;
      const sy = frame.height / el.videoHeight;
      const scaled = found && {
        ...found,
        corners: found.corners.map((c) => ({ x: c.x * sx, y: c.y * sy })) as Detection['corners'],
      };
      onCapture(await prepareReceipt({ source: frame, width: frame.width, height: frame.height }, scaled));
    } catch {
      // Never leave the user stuck on "Got it": send the frame as it is, or
      // fall back to the phone's own photo picker.
      const blob = frame ? await new Promise<Blob | null>((r) => frame!.toBlob(r, 'image/jpeg', 0.88)) : null;
      stream.current?.getTracks().forEach((track) => track.stop());
      if (blob) onCapture(blob);
      else onUnavailable();
    } finally {
      if (frame) release(frame);
    }
  };

  // Detection loop: outline the receipt; capture once it has been steady and sharp.
  useEffect(() => {
    let timer = 0;
    let steadyCount = 0;
    const recentSharpness: number[] = [];
    const tick = () => {
      const el = video.current;
      if (el && el.videoWidth && !captured.current) {
        let analysis;
        try {
          analysis = analyzeFrame({ source: el, width: el.videoWidth, height: el.videoHeight });
        } catch {
          // A dropped frame (e.g. canvas memory pressure) must not stop the loop.
          timer = window.setTimeout(tick, FRAME_MS * 3);
          return;
        }
        const found = analysis.detection;
        const sharp = sharpness(analysis.gray, analysis.w, analysis.h);
        recentSharpness.push(sharp);
        if (recentSharpness.length > 15) recentSharpness.shift();

        const previous = lastDetection.current;
        const moved =
          !found || !previous
            ? Infinity
            : found.corners.reduce((sum, c, i) => sum + Math.hypot(c.x - previous.corners[i]!.x, c.y - previous.corners[i]!.y), 0) /
              4 /
              el.videoWidth;
        const focused = sharp >= 0.6 * Math.max(...recentSharpness);
        // Only a receipt that is fully in view (and steady, and in focus) counts.
        steadyCount =
          found?.complete && moved < 0.015 && focused ? steadyCount + 1 : found ? Math.max(0, steadyCount - 2) : 0;

        lastDetection.current = found;
        setDetection(found);
        setSteady(steadyCount);
        setStatus(steadyCount > 2 ? 'steady' : found && !found.complete ? 'partial' : 'searching');
        if (steadyCount >= STEADY_FRAMES) void capture(found);
      }
      timer = window.setTimeout(tick, FRAME_MS);
    };
    timer = window.setTimeout(tick, FRAME_MS);
    return () => window.clearTimeout(timer);
    // capture only reads refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleTorch = async () => {
    const [track] = stream.current?.getVideoTracks() ?? [];
    if (!track || torch === null) return;
    await track.applyConstraints({ advanced: [{ torch: !torch } as MediaTrackConstraintSet] }).catch(() => undefined);
    setTorch(!torch);
  };

  // Map detection corners (video pixels) onto the on-screen preview (object-fit: cover).
  const el = video.current;
  let outline: string | null = null;
  if (detection && el?.videoWidth) {
    const box = el.getBoundingClientRect();
    const scale = Math.max(box.width / el.videoWidth, box.height / el.videoHeight);
    const offsetX = (box.width - el.videoWidth * scale) / 2;
    const offsetY = (box.height - el.videoHeight * scale) / 2;
    outline = detection.corners.map((c) => `${c.x * scale + offsetX},${c.y * scale + offsetY}`).join(' ');
  }
  const progress = Math.min(1, steady / STEADY_FRAMES);

  return createPortal(
    <div className="camera" role="dialog" aria-modal="true" aria-label={t('receipt.scan')}>
      <video ref={video} className="camera-video" playsInline muted />
      <svg className="camera-overlay" aria-hidden>
        {outline ? (
          <polygon
            points={outline}
            className={`camera-outline ${status === 'steady' || status === 'captured' ? 'camera-outline--steady' : ''} ${status === 'partial' ? 'camera-outline--partial' : ''}`}
          />
        ) : (
          <rect className="camera-guide" x="12%" y="14%" width="76%" height="62%" rx="22" />
        )}
      </svg>

      <div className="camera-top">
        <button type="button" className="camera-icon-btn" onClick={onClose} aria-label={t('common.close')}>
          <Icon name="close" size={22} />
        </button>
        <div className="camera-status" aria-live="polite">
          {status === 'captured'
            ? t('camera.captured')
            : status === 'steady'
              ? t('camera.holdStill')
              : status === 'partial'
                ? t('camera.fitWhole')
                : t('camera.findReceipt')}
        </div>
        {torch !== null ? (
          <button
            type="button"
            className={`camera-icon-btn ${torch ? 'camera-icon-btn--on' : ''}`}
            onClick={() => void toggleTorch()}
            aria-label={t('camera.torch')}
            aria-pressed={torch}
          >
            <Icon name="flash" size={20} />
          </button>
        ) : (
          <span className="camera-icon-btn camera-icon-btn--ghost" />
        )}
      </div>

      <div className="camera-bottom">
        <button type="button" className="camera-icon-btn" onClick={onPickPhoto} aria-label={t('camera.gallery')}>
          <Icon name="image" size={22} />
        </button>
        <button
          type="button"
          className="camera-shutter"
          style={{ ['--progress' as string]: progress }}
          onClick={() => void capture(lastDetection.current)}
          disabled={status === 'captured'}
          aria-label={t('camera.shutter')}
        >
          <span />
        </button>
        <span className="camera-icon-btn camera-icon-btn--ghost" />
      </div>
      <div className="camera-tip">{t('camera.tip')}</div>
      {status === 'captured' && <div className="camera-flash" aria-hidden />}
    </div>,
    document.body,
  );
}
